import React from 'react';
import { systemText } from '../../../helpers/string';
import { sortData } from '../../../helpers/treeview';
import '../forms.css';
import './select.css';
import FormLabel from '../formLabel';
import uuid from 'uuid/v4';

export const Select = props => {
	const [showDropdown, setShowDropdown] = React.useState(false);
	const [open, setOpen] = React.useState([]);
	const [value, setValue] = React.useState(props.value);
	const [search, setSearch] = React.useState('');

	// Using a supplied controlId as a unique identifier for simplicity and stability to prevent rendering issues that move scroll position, if one is bot provided a new UUID is generated.
	const controlId = props.controlId || uuid();

	const outer = React.useRef(null);
	const searchControl = React.useRef(null);

	React.useEffect(() => {
		document.addEventListener('mouseup', handleClick, false);

		if (props.isTree) {
			props.data.forEach(d => {
				if ([].concat(props.value).some(v => v === d.id)) {
					d.parent_Id && openUp(d.parent_Id);
				}
			});
		}

		return () => {
			document.removeEventListener('mouseup', handleClick, false);
		};
	}, []);

	React.useEffect(() => {
		const temp =
			props.value && Array.isArray(props.value)
				? props.value.filter(v => props.data.some(d => d.id === v))
				: props.value;
		setValue(temp);
	}, [props.value, props.data]);

	const checkDisabledValue = id => {
		const { disabledValues, dataSetValue } = props;
		if (!disabledValues && !dataSetValue) return false;
		return id === dataSetValue || (disabledValues && disabledValues.filter(d => d === id).length > 0);
	};

	const clear = () => {
		setShowDropdown(false);
		setSearch('');
		setValue(props.allowMultiple ? [] : null);

		if (typeof props.onChange === 'function') {
			props.onChange(props.allowMultiple ? [] : null);
		}
	};

	const setItemValue = (e, d) => {
		let temp;
		const { data } = props;
		if (props.allowMultiple) {
			temp = value ? [...value.filter(v => data.filter(d => d.id === v).length > 0)] : [];
			if (e.target.checked && temp.indexOf(d.id) < 0) {
				temp = temp.concat([d.id]);
			} else {
				let index = temp.indexOf(d.id);
				if (index > -1) {
					temp.splice(index, 1);
				}
			}

			setValue(temp);
		} else {
			temp = value;
			setValue(d.id);
		}

		if (!props.allowMultiple) {
			toggle();
		}

		props.onChange && props.onChange(props.allowMultiple ? temp : d.id);
	};

	const toggle = () => {
		setShowDropdown(!showDropdown);
		setSearch('');
		searchControl.current?.focus();
	};

	const openMe = (id, callback) => {
		setOpen(open.concat(id));
		callback && callback();
	};

	const close = id => {
		setShowDropdown(open.pop(id));
	};

	const checkSubmit = e => {
		if (e.key === 'Enter') {
			toggle();
			if (typeof props.onSubmit === 'function') {
				props.onSubmit();
			}
		}
	};

	const openUp = id => {
		const elm = props.data.filter(d => d.id === id)[0];
		openMe(id, () => elm.parent_Id && openUp(elm.parent_Id));
	};

	const handleClick = e => {
		if (e.target.id !== 'modal-body' && !outer.current?.contains(e.target)) {
			setShowDropdown(false);
		}
	};

	const {
		dataBlockValue,
		dataSetValue,
		label,
		help,
		infoHelp,
		placeholder,
		error,
		warning,
		id,
		data,
		allowMultiple,
		blockFilter,
		mandatory,
		isTree,
		readonly,
		filters,
		inline,
	} = props;

	let filteredData = data;

	if (dataBlockValue) {
		filteredData = data.reduce((c, d) => {
			return d.id === dataBlockValue ? c : c.concat(d);
		}, []);
	}

	if (dataSetValue && value.filter(v => v === dataSetValue).length === 0) {
		value.push(dataSetValue);
	}

	if (search) {
		filteredData = filteredData.reduce((c, d) => {
			return d.name.toLowerCase().indexOf(search.toLowerCase()) < 0 ? c : c.concat(d);
		}, []);
	}

	const valueText =
		allowMultiple && value
			? value.length === 0
				? ''
				: systemText('General/Controls/Select/Selected', value.length)
			: filteredData && filteredData.reduce((c, d) => (d.id === value ? d.name : c), '');

	const checkOpen = id => {
		if (!id) return true;
		return (
			id === 0 ||
			open.reduce((c, o) => {
				if (o === id) {
					return true;
				}

				return c;
			}, false)
		);
	};

	const prepared = isTree ? sortData(filteredData) : filteredData;

	const maxOptionToDisplay = props.maxOptionToDisplay || 100;

	const dataRows =
		prepared &&
		prepared.map((d, i) => {
			if (i === maxOptionToDisplay) {
				const extra = prepared.length - maxOptionToDisplay;
				return (
					<div key="more-rows" data-key="more-rows" style={{ padding: '0 5px', fontStyle: 'italic' }}>
						{systemText('General/Controls/Select/MoreRows', extra)}
					</div>
				);
			}

			if (i > maxOptionToDisplay) {
				return null;
			}

			const key = `option-${id || controlId}-${d.id || ''}`;
			const isOpen = checkOpen(d.id);
			if (isTree && !checkOpen(d.parent_Id)) return null;
			return (
				<span key={key} data-key={key}>
					<div className="flex flex-center" style={{ paddingLeft: `${20 * d.level}px` }}>
						{!d.children && isTree && <i className="fa fa-plus-square invisible" />}
						{d.children === true && !isOpen && <i className="fa fa-plus-square" onClick={() => openMe(d.id)} />}
						{d.children === true && isOpen && <i className="fa fa-minus-square" onClick={() => close(d.id)} />}
						<label htmlFor={key} className="flex flex-center">
							{allowMultiple && (
								<input
									type="checkbox"
									onChange={e => setItemValue(e, d)}
									onKeyDown={e => checkSubmit(e)}
									checked={(value || []).some(v => v === d.id)}
									value={d.id ? d.id : ''}
									id={key}
									disabled={d.disabled || checkDisabledValue(d.id)}
								/>
							)}
							{!allowMultiple && (
								<input
									type="checkbox"
									onChange={e => setItemValue(e, d)}
									onKeyDown={e => checkSubmit(e)}
									checked={d.id === (value || '')}
									value={d.id ? d.id : ''}
									id={key}
									name={id || 'options'}
									disabled={d.disabled || checkDisabledValue(d.id)}
								/>
							)}
							<span className="flex-grow">
								{d.name}
								{d.extra && (
									<span className="ml5 text-sm">
										{typeof d.extra === 'function' ? d.extra() : <span>({d.extra})</span>}
									</span>
								)}
								{d.extraNewLine && <div className="text-sm">{d.extraNewLine}</div>}
							</span>
						</label>
					</div>
				</span>
			);
		});

	const allowFilters = false;

	let showClear = !mandatory;
	if (allowMultiple) {
		showClear = showClear && value && value.length > 0;
	} else {
		showClear = showClear && (value || value === 0);
	}

	const dropdown = (
		<div ref={outer} className="select-container">
			<input
				ref={searchControl}
				id={`select-${id || controlId}`}
				type="text"
				readOnly={readonly}
				className={`form-control select-initiator ${props.className}`}
				placeholder={showDropdown && !blockFilter ? systemText('General/Controls/Select/Filter') : placeholder}
				value={showDropdown ? search : valueText ? valueText : ''}
				onChange={e => setSearch(e.target.value)}
				onClick={() => !readonly && toggle()}
				onKeyUp={e => e.key === 'Enter' && !readonly && toggle()}
				onFocus={blockFilter ? e => e.target.blur() : undefined}
				autoComplete="off"
			/>
			{!readonly && (
				<span className="select-clear text-sm flex flex-center">
					{showClear && (
						<button className="btn-link mr10" onClick={() => clear()}>
							{systemText('General/Controls/Select/Clear')}
						</button>
					)}
					<button className="btn-link" onClick={() => toggle()}>
						<i className="fa fa-sort-down" />
					</button>
				</span>
			)}
			{showDropdown && (
				<div className="form-control select-display">
					{allowFilters && filters && <div className="filters-current">{filters.current}</div>}
					{allowFilters && filters && <div className="filters-available">{filters.available}</div>}
					{data && dataRows}
				</div>
			)}
		</div>
	);

	return inline ? (
		dropdown
	) : (
		<div
			key={id}
			id={id}
			data-testid={props['data-testid']}
			className={`form-group ${error ? 'has-error' : warning ? 'has-warning' : ''}`}
		>
			<FormLabel type="select" {...props} htmlFor={`select-${id || controlId}`} />
			{dropdown}
			{error && <span className="help-block">{typeof error === 'function' ? error() : error}</span>}
			{warning && <span className="help-block">{typeof warning === 'function' ? warning() : warning}</span>}
		</div>
	);
};

export default Select;
